import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import {MatSelectChange} from '@angular/material/select';
import {ReplaySubject} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  takeUntil
} from 'rxjs/operators';

import {
  DobryAgentBlogTag
} from '../../interface/dobry-agent-blog-article';

import {
  DobryAgentBlogTagService
} from '../../services/dobry-agent-blog-tag.service';

import {
  CountedAndPaginated
} from '../../interface/response/counted-and-paginated';

@Component({
  selector: 'app-dict-dobry-agent-blog-tags',
  templateUrl: './dict-dobry-agent-blog-tags.component.html',
  styleUrls: ['./dict-dobry-agent-blog-tags.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(
        () => DictDobryAgentBlogTagsComponent
      )
    }
  ]
})
export class DictDobryAgentBlogTagsComponent
  implements OnInit, OnDestroy, ControlValueAccessor {

  @Input() label = 'Tagi';
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() hiddenChips = false;

  @Output()
  selectedTags = new EventEmitter<DobryAgentBlogTag[]>();

  items: DobryAgentBlogTag[] = [];

  /**
   * Osobno przechowujemy wybrane obiekty,
   * także gdy nie występują już na aktualnej stronie.
   */
  selectedItems: DobryAgentBlogTag[] = [];

  filterControl: FormControl;

  loading = false;
  loadingMore = false;

  page = 0;
  readonly pageSize = 20;

  total = 0;
  search = '';

  externalControl = true;
  disabled = false;

  value: string[] = [];

  private destroyed$ = new ReplaySubject<boolean>(1);

  constructor(
    private blogTagService: DobryAgentBlogTagService,
    private fb: FormBuilder,
  ) {
    this.filterControl = this.fb.control('');
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl([]);
      this.externalControl = false;
    }

    this.filterControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((value: string | null) => {
        this.search = (value ?? '').trim();

        this.loadFirstPage();
      });

    this.control.valueChanges
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((value: string[] | null) => {
        this.value = Array.isArray(value) ? value : [];

        this.syncSelectedItems();
        this.ensureSelectedTagsLoaded();
      });

    this.value = Array.isArray(this.control.value)
      ? this.control.value
      : [];

    this.loadFirstPage();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: string[] | null): void {
    if (this.externalControl) {
      return;
    }

    this.value = Array.isArray(value) ? value : [];

    this.control.setValue(
      this.value,
      {emitEvent: false}
    );
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;

    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable({emitEvent: false});
      return;
    }

    this.control.enable({emitEvent: false});
  }

  onSelectionChange(event: MatSelectChange): void {
    const value = Array.isArray(event.value)
      ? event.value
      : [];

    this.value = value;

    this.onChange(this.value);
    this.onTouched();

    this.syncSelectedItems();
    this.ensureSelectedTagsLoaded();
  }

  removeTag(
    tag: DobryAgentBlogTag,
    event?: MouseEvent
  ): void {
    event?.preventDefault();
    event?.stopPropagation();

    const value = this.getSelectedUuids()
      .filter(uuid => uuid !== tag.uuid);

    this.control.setValue(value);

    this.value = value;

    this.selectedItems = this.selectedItems.filter(
      item => item.uuid !== tag.uuid
    );

    this.onChange(value);
    this.onTouched();

    this.selectedTags.emit([...this.selectedItems]);
  }

  clearValue(): void {
    this.control.setValue([]);

    this.value = [];
    this.selectedItems = [];

    this.onChange([]);
    this.onTouched();

    this.selectedTags.emit([]);
  }

  loadMore(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.hasMore || this.loadingMore) {
      return;
    }

    this.page++;

    this.loadPage(true);
  }

  get hasMore(): boolean {
    return this.items.length < this.total;
  }

  get hasSelectedTags(): boolean {
    return this.selectedItems.length > 0;
  }

  private loadFirstPage(): void {
    this.page = 0;
    this.items = [];
    this.total = 0;

    this.loadPage(false);
  }

  private loadPage(append: boolean): void {
    if (append) {
      this.loadingMore = true;
    } else {
      this.loading = true;
    }

    const filters: Record<string, any> = {
      active: true,
    };

    if (this.search) {
      filters.context = this.search;
    }

    this.blogTagService
      .searchList(
        this.page,
        this.pageSize,
        filters,
        {
          sortBy: 'name',
          sortOrder: 'ASC'
        }
      )
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: (
          response: CountedAndPaginated<DobryAgentBlogTag>
        ) => {
          const newItems = response.items;

          this.total = response.pagination.total;

          this.items = append
            ? this.mergeUnique(this.items, newItems)
            : newItems;

          this.loading = false;
          this.loadingMore = false;

          this.syncSelectedItems();
          this.ensureSelectedTagsLoaded();
        },
        error: () => {
          this.loading = false;
          this.loadingMore = false;
        }
      });
  }

  private getSelectedUuids(): string[] {
    return Array.isArray(this.control.value)
      ? this.control.value
      : [];
  }

  private syncSelectedItems(): void {
    const selectedUuids = this.getSelectedUuids();

    /**
     * Łączymy aktualne wyniki z wcześniej załadowanymi
     * wybranymi tagami.
     */
    const availableItems = this.mergeUnique(
      this.selectedItems,
      this.items
    );

    this.selectedItems = availableItems.filter(
      tag => selectedUuids.includes(tag.uuid)
    );

    this.selectedTags.emit([...this.selectedItems]);
  }

  private ensureSelectedTagsLoaded(): void {
    const selectedUuids = this.getSelectedUuids();

    if (!selectedUuids.length) {
      this.selectedItems = [];
      return;
    }

    const loadedUuids = new Set(
      this.mergeUnique(this.items, this.selectedItems)
        .map(tag => tag.uuid)
    );

    const missingUuids = selectedUuids.filter(
      uuid => !loadedUuids.has(uuid)
    );

    missingUuids.forEach(uuid => {
      this.blogTagService
        .get(uuid)
        .pipe(
          takeUntil(this.destroyed$)
        )
        .subscribe({
          next: tag => {
            this.selectedItems = this.mergeUnique(
              this.selectedItems,
              [tag]
            );

            this.selectedTags.emit([
              ...this.selectedItems
            ]);
          }
        });
    });
  }

  private mergeUnique(
    current: DobryAgentBlogTag[],
    next: DobryAgentBlogTag[]
  ): DobryAgentBlogTag[] {
    const map = new Map<string, DobryAgentBlogTag>();

    [...current, ...next].forEach(tag => {
      map.set(tag.uuid, tag);
    });

    return Array.from(map.values());
  }
}
